import { DatePipe, LowerCasePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { AudioTrackingComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/audio-tracking/audio-tracking.component';
import {
  IKeywordCard,
  IKpiCard,
} from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { LineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.component';
import { ILineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { IAudioTrackingAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EmbeddedVideoPlayerComponent } from '@shared/components/embedded-video-player/embedded-video-player.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { FindingVisualizerService } from '@utilities/finding-visualizer.service';
import { StreamDetailsDeliverableBaseDirective } from '../../stream-details-deliverable-base.directive';
import { IAudioTrackingStreamAnalyticsTableData } from './audio-tracking-stream-analytics.interface';

@Component({
  imports: [
    DatePipe,
    TranslateModule,
    LineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    EmbeddedVideoPlayerComponent,
    AudioTrackingComponent,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
    MatMenuModule,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-audio-tracking-stream-analytics',
  templateUrl: './audio-tracking-stream-analytics.component.html',
})
export class AudioTrackingStreamAnalyticsComponent extends StreamDetailsDeliverableBaseDirective<
  IAudioTrackingStreamAnalyticsTableData,
  IAudioTrackingAnalytics
> {
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'audioTracking-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.audioTracking.totalCount'),
    value: 0,
  };
  public override readonly displayedColumns: string[] = ['timestamp', 'videoTime', 'title', 'actions'];
  public override readonly dataSource: MatTableDataSource<IAudioTrackingStreamAnalyticsTableData> =
    new MatTableDataSource();
  public override readonly deliverableType = EDeliverableType.audioTracking;
  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.audioTrackings?.find((at) => at.deliverableId === deliverableId);
    }

    return null;
  });

  public readonly keywordList = signal<IKeywordCard[]>([]);
  public readonly keywordCardList = signal<IKpiCard[]>([]);
  public readonly allKpiCards = computed(() => [this.kpiCardData, ...this.keywordCardList()]);

  constructor() {
    super();

    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setCounts();
        this.setStatus();
        this.setStreamData();
        this.setKeywordList();
      }
    });

    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings() && this.timelineData()) {
        this.prepareChartAndTableData();
      }
    });

    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        const audioTracking = this.currentDeliverable();

        this.keywords = audioTracking?.keywords?.map((audio) => audio.keyword) ?? [];
      }
    });
  }

  private readonly findingVisualizerService = inject(FindingVisualizerService);

  protected override prepareChartAndTableData(): void {
    const dotGraphData: ILineGraphInputData[] = [];
    const keywords = this.currentDeliverable()?.keywords ?? [];
    const timelineData = this.timelineData() ?? [];
    const { sortedDictionary } = this.graphUtilities.processTimelineData(
      timelineData,
      this.stream()?.executionTime,
      this.stream()?.end,
    );
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const visualizers = this.findingVisualizerService.buildFromKeywords(keywords, removedIds);

    const tableData: IAudioTrackingStreamAnalyticsTableData[] = visualizers.map((visualizer) => {
      const [primaryFinding] = visualizer.findings;

      const kwAnalytics = keywords.find((k) => k.keywordId === primaryFinding.parentId);
      const primaryTimestamp = this.dateUtils.parseDateTimeFromApi(primaryFinding.timestamp).getTime();
      const interpolatedViewCount = this.graphUtilities.getInterpolatedViewCount(sortedDictionary, primaryTimestamp);

      dotGraphData.push({
        id: visualizer.id,
        status: kwAnalytics?.status ?? primaryFinding.status,
        xAxisValue: primaryTimestamp,
        yAxisValue: Math.round(interpolatedViewCount),
      });

      return {
        ...visualizer,
        text: primaryFinding.audio,
        timestamp: primaryFinding.timestamp,
        videoTimeLabel: AnalyticsDetailsUtilitiesService.formatVideoTime(primaryFinding.videoTime),
        videoTimeSeconds: primaryFinding.videoTime ?? 0,
      };
    });

    const sortedTableData = [...tableData].sort(
      (a, b) =>
        this.dateUtils.parseDateTimeFromApi(a.timestamp).getTime() -
        this.dateUtils.parseDateTimeFromApi(b.timestamp).getTime(),
    );

    this.dataSource.data = sortedTableData;
    this.dataCount.set(sortedTableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
    this.setKeywordTableRowIds(this.findingVisualizerService.buildKeywordRowIdMap(sortedTableData));
  }

  protected override setCounts(): void {
    const audioTrackingAnalytics = this.currentDeliverable();

    const keywords = audioTrackingAnalytics?.keywords ?? [];
    const keywordNames = keywords.map((k) => k.keyword);
    const findings = keywords.flatMap((k) => k.findings ?? []);
    const uniqueFindings = AnalyticsDetailsUtilitiesService.getUniqueTextFindings(findings) as string[];

    const counts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywordNames, uniqueFindings);
    const total = Object.values(counts).reduce((sum, val) => sum + val, 0);
    this.kpiCardData.value = total;

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), counts);
  }

  protected override setKeywordList(): void {
    const audioTrackingAnalytics = this.currentDeliverable();

    if (audioTrackingAnalytics?.keywords && audioTrackingAnalytics.keywords.length > 0) {
      const { keywordList, kpiCardList } = AnalyticsDetailsUtilitiesService.buildKeywordLists(
        audioTrackingAnalytics.keywords,
        'keyword',
        'findings',
        'equalizer.svg',
      );
      this.keywordList.set(keywordList);
      this.keywordCardList.set(kpiCardList);
    }
  }
}
